import type { BattleProcedureStep } from "./steps";

/**
 * Pure battle-procedure run state. R4.0 sequences and times the trainee and
 * captures what he produces. It does not score, mark or assess any of it.
 */
export interface BattleProcedureStepRecord {
  /** Set only by an explicit trainee confirmation on a mandatory step. */
  readonly confirmed: boolean;
  readonly completedAtMs: number | null;
  /** Frozen once the step is completed; null while it is still running. */
  readonly elapsedMs: number | null;
  readonly startedAtMs: number | null;
  readonly stepId: string;
  readonly text: string;
  readonly textUpdatedAtMs: number | null;
}

export type BattleProcedureRunStatus = "finished" | "idle" | "running";

export interface BattleProcedureRunState {
  readonly currentIndex: number;
  /** When the trace was issued, on the run clock. Null until it is. */
  readonly traceRevealedAtMs: number | null;
  readonly finishedAtMs: number | null;
  readonly schemaVersion: 1;
  readonly startedAtMs: number | null;
  readonly status: BattleProcedureRunStatus;
  readonly steps: readonly BattleProcedureStepRecord[];
}

export type BattleProcedureAction =
  | { readonly type: "ADVANCE" }
  | { readonly type: "CONFIRM"; readonly stepId: string }
  | {
      readonly type: "SET_TEXT";
      readonly stepId: string;
      readonly text: string;
    }
  | { readonly type: "START" };

export function createRunState(
  steps: readonly BattleProcedureStep[],
): BattleProcedureRunState {
  if (steps.length === 0) {
    throw new RangeError("a battle procedure run needs at least one step");
  }
  return {
    currentIndex: 0,
    traceRevealedAtMs: null,
    finishedAtMs: null,
    schemaVersion: 1,
    startedAtMs: null,
    status: "idle",
    steps: steps.map((step) => ({
      confirmed: false,
      completedAtMs: null,
      elapsedMs: null,
      startedAtMs: null,
      stepId: step.id,
      text: "",
      textUpdatedAtMs: null,
    })),
  };
}

/**
 * Whether the trainee may leave the current step. A step that requires
 * confirmation is a mandatory stop: it cannot be passed until he actively
 * confirms it. Nothing else blocks advancing, because no other completion
 * rule is established in doc 03.
 */
export function canAdvance(
  state: BattleProcedureRunState,
  steps: readonly BattleProcedureStep[],
): boolean {
  if (state.status !== "running") return false;
  const step = steps[state.currentIndex];
  const record = state.steps[state.currentIndex];
  /* v8 ignore next -- currentIndex is always inside both arrays */
  if (step === undefined || record === undefined) return false;
  return step.requiresConfirmation !== true || record.confirmed;
}

function withStep(
  state: BattleProcedureRunState,
  index: number,
  change: Partial<BattleProcedureStepRecord>,
): BattleProcedureRunState {
  return {
    ...state,
    steps: state.steps.map((record, position) =>
      position === index ? { ...record, ...change } : record,
    ),
  };
}

function indexOfStep(
  state: BattleProcedureRunState,
  stepId: string,
): number | null {
  const index = state.steps.findIndex((record) => record.stepId === stepId);
  return index < 0 ? null : index;
}

export function transitionRun(
  state: BattleProcedureRunState,
  action: BattleProcedureAction,
  nowMs: number,
  steps: readonly BattleProcedureStep[],
): BattleProcedureRunState {
  switch (action.type) {
    case "START": {
      if (state.status !== "idle") return state;
      return withStep({ ...state, startedAtMs: nowMs, status: "running" }, 0, {
        startedAtMs: nowMs,
      });
    }
    case "SET_TEXT": {
      const index = indexOfStep(state, action.stepId);
      if (index === null || state.status === "finished") return state;
      return withStep(state, index, {
        text: action.text,
        textUpdatedAtMs: nowMs,
      });
    }
    case "CONFIRM": {
      const index = indexOfStep(state, action.stepId);
      if (index === null || state.status !== "running") return state;
      return withStep(state, index, { confirmed: true });
    }
    case "ADVANCE": {
      if (!canAdvance(state, steps)) return state;
      const record = state.steps[state.currentIndex];
      /* v8 ignore next -- canAdvance already proved the record exists */
      if (record === undefined) return state;
      const completed = withStep(state, state.currentIndex, {
        completedAtMs: nowMs,
        elapsedMs: Math.max(
          0,
          /* v8 ignore next -- the running step always carries a start time */
          nowMs - (record.startedAtMs ?? nowMs),
        ),
      });
      const next = state.currentIndex + 1;
      if (next >= state.steps.length) {
        return { ...completed, finishedAtMs: nowMs, status: "finished" };
      }
      const entering = steps[next];
      const revealed =
        entering?.issuesTrace === true && state.traceRevealedAtMs === null
          ? nowMs
          : state.traceRevealedAtMs;
      return withStep(
        { ...completed, currentIndex: next, traceRevealedAtMs: revealed },
        next,
        { startedAtMs: nowMs },
      );
    }
  }
}

/** Elapsed time on one step, live for the running step and frozen after. */
export function stepElapsedMs(
  state: BattleProcedureRunState,
  index: number,
  nowMs: number,
): number {
  const record = state.steps[index];
  if (record === undefined) return 0;
  if (record.elapsedMs !== null) return record.elapsedMs;
  if (record.startedAtMs === null) return 0;
  return Math.max(0, nowMs - record.startedAtMs);
}

/** Elapsed time on the whole run, from step 1 until it finishes. */
export function runElapsedMs(
  state: BattleProcedureRunState,
  nowMs: number,
): number {
  if (state.startedAtMs === null) return 0;
  const end = state.finishedAtMs ?? nowMs;
  return Math.max(0, end - state.startedAtMs);
}

export interface CountdownState {
  /** Negative once the standard has been exceeded. */
  readonly remainingMs: number;
  readonly standardMs: number;
  readonly withinStandard: boolean;
}

/**
 * The countdown against a step's own stated standard, such as the twenty
 * minutes allowed for issuing orders. Returns null for a step with no
 * standard; no default standard is assumed for any other step.
 */
export function stepCountdown(
  state: BattleProcedureRunState,
  index: number,
  nowMs: number,
  steps: readonly BattleProcedureStep[],
): CountdownState | null {
  const step = steps[index];
  if (step?.countdownMinutes === undefined) return null;
  const standardMs = step.countdownMinutes * 60_000;
  const remainingMs = standardMs - stepElapsedMs(state, index, nowMs);
  return { remainingMs, standardMs, withinStandard: remainingMs >= 0 };
}

export function formatElapsed(totalMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(totalMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");
}

export interface TraceGateState {
  /** Negative once the lock has expired. */
  readonly remainingMs: number;
  readonly lockMs: number;
  readonly ordersUnlocked: boolean;
  readonly traceVisible: boolean;
}

/**
 * The trace gate. Doc 03 section 7.3: the trainee has fifteen minutes to copy
 * a trace before orders.
 *
 * The trace is hidden until the run reaches the step that issues it, then
 * stays visible to the end of the run because he is marking it. From the
 * reveal a lock runs on the run clock, and orders delivery is locked until it
 * expires. No scenario-clock anchor is used or needed.
 */
export function traceGateState(
  state: BattleProcedureRunState,
  nowMs: number,
  lockMinutes: number,
): TraceGateState {
  const lockMs = lockMinutes * 60_000;
  if (state.traceRevealedAtMs === null) {
    return {
      lockMs,
      ordersUnlocked: false,
      remainingMs: lockMs,
      traceVisible: false,
    };
  }
  const remainingMs = lockMs - Math.max(0, nowMs - state.traceRevealedAtMs);
  return {
    lockMs,
    ordersUnlocked: remainingMs <= 0,
    remainingMs,
    traceVisible: true,
  };
}
